import React, { useState } from "react";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";
import { Plus, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ASG_CHIP, docApi, docPref, isPmc, isSa } from "@/lib/docApi";
import { Empty, ErrorBox, Loading, StatusFilter, SummaryChip, btnGold, useLoad, when } from "@/components/nds/windows/finance/parts";
import { RecipientInbox, StatusChip } from "@/components/nds/windows/doc/parts";
import AssignForm from "@/components/nds/windows/doc/AssignForm";
import AssignmentDetail from "@/components/nds/windows/doc/AssignmentDetail";

function AllAssignments({ user }) {
  const [status, setStatusState] = useState(() => docPref.get("asg_status", "all"));
  const setStatus = (v) => { docPref.set("asg_status", v); setStatusState(v); };
  const [view, setView] = useState(null);
  const [created, setCreated] = useState(null);
  const [state, load] = useLoad(() => Promise.all([docApi.get("/assignments"), docApi.get("/documents")]).then(([a, d]) => ({ ...a, ...d })), []);
  if (state.loading && !state.data) return <Loading testid="doc-asg-loading" />;
  if (state.error) return <ErrorBox testid="doc-asg-error" error={state.error} onRetry={load} />;
  const { assignments, documents } = state.data;
  if (view === "new") return <AssignForm documents={documents} onCancel={() => setView(null)} onCreated={(d) => { setCreated(d); load(); setView(d.id); }} />;
  const rec = view && assignments.find((a) => a.id === view);
  if (rec) return <AssignmentDetail rec={rec} sa={isSa(user)} created={created?.id === rec.id ? created : null} onBack={() => { setView(null); setCreated(null); }} onChanged={load} />;
  const counts = assignments.reduce((a, x) => ({ ...a, [x.status]: (a[x.status] || 0) + 1 }), { all: assignments.length });
  const list = assignments.filter((a) => status === "all" || a.status === status);
  return (
    <>
      <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
        <SummaryChip tone="yellow" testid="doc-asg-summary">{(counts.pending || 0) + (counts.viewed || 0)} pending</SummaryChip>
        <StatusFilter prefix="doc-asg" chips={ASG_CHIP} value={status} onChange={setStatus} counts={counts} />
        {isPmc(user) && <button type="button" onClick={() => setView("new")} data-testid="doc-asg-new" className={`${btnGold} ml-auto inline-flex items-center gap-1`}><Plus className="h-3 w-3" /> Assign</button>}
      </div>
      <ul data-testid="doc-asg-list" className="flex-1 overflow-y-auto divide-y divide-white/5">
        {list.map((a) => (
          <li key={a.id}>
            <button type="button" onClick={() => setView(a.id)} data-testid={`doc-asg-row-${a.id}`} className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-nex-gold/5">
              <span className="flex-1 min-w-0 truncate text-nex-silver font-medium">{a.document_title}</span>
              <span className="text-xs text-nex-muted truncate max-w-[180px]">{a.recipient_name} · {a.recipient_type}</span>
              <span className="font-mono text-[10px] text-nex-muted hidden sm:inline">{when(a.created_at)}</span>
              <StatusChip map={ASG_CHIP} status={a.status} />
            </button>
          </li>
        ))}
        {!list.length && <li><Empty testid="doc-asg-empty">No assignments here.</Empty></li>}
      </ul>
    </>
  );
}

const TABS = [["all", "All assignments"], ["mine", "My Documents"]];

export default function DocAssignmentsContent() {
  const { user } = useAuth();
  const ct = useCardTabs(docPref.get("asg_tab", "all"), (v) => docPref.set("asg_tab", v));
  const tab = ct.tab;
  const setTab = ct.open;
  const items = [{ id: "all", label: "All assignments", tone: "ops", desc: "Every document sent out and who still has to sign" }, { id: "mine", label: "My Documents", tone: "comms", desc: "Documents assigned to you" }];
  if (ct.deck) return <div ref={ct.ref} data-testid="doc-asg" className="p-5"><CardDeck testid="doc-asg-cards" items={items} onOpen={setTab} /></div>;
  return (
    <div ref={ct.ref} data-testid="doc-asg" className="flex flex-col h-full">
      {ct.compact && <div className="px-5 pt-4"><CardsBack testid="doc-asg-cards-back" className="mb-2" label={TABS.find(([k]) => k === tab)[1]} onClick={ct.close} /></div>}
      <div className={`px-5 pt-3 flex gap-4 border-b border-white/10 ${ct.compact ? "hidden" : ""}`}>
        <Send className="h-4 w-4 text-nex-gold" />
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`doc-asg-tab-${k}`} aria-pressed={tab === k}
            className={`pb-2 font-mono text-[10px] tracking-[0.2em] uppercase border-b-2 ${tab === k ? "border-nex-gold text-nex-gold" : "border-transparent text-nex-muted hover:text-nex-silver"}`}>{l}</button>
        ))}
      </div>
      {tab === "all" ? <AllAssignments user={user} /> : <div className="p-5 overflow-y-auto"><RecipientInbox api={docApi} listPath="/my/assignments" prefix="doc-mine" /></div>}
    </div>
  );
}
